import {catchError, map, Observable} from 'rxjs';

import {signal, WritableSignal} from '@angular/core';
import {CycleListType, ICycle, IGetCycleCountRequest, IGetCycleCountResponse, IGetCycleListRequest, IGetCycleListResponse} from '@core/api/data-access';
import {capitalize, objectify} from '@utils';

import {CyclesService} from './cycles.service';
import dayjs from 'dayjs';

export class ListCyclesService {
	public readonly cycleListsCounts$: WritableSignal<Map<CycleListType, number>> = signal(new Map<CycleListType, number>());

	constructor(private cyclesService: CyclesService) {}

	/**
	 * This service method is used to get the count of cycles.
	 *
	 * @param fromDate
	 * @param toDate
	 * @param metaTags
	 *
	 * @returns {Observable<Map<CycleListType, number>>} An Observable of mapping listType for cycles count.
	 */
	getCyclesCount(fromDate?: Date, toDate?: Date, metaTags?: Map<string, string>): Observable<Map<CycleListType, number>> {
		const request: IGetCycleCountRequest = {
			fromDate: fromDate ? (dayjs(fromDate).format() as any) : undefined,
			toDate: toDate ? (dayjs(toDate).format() as any) : undefined,
			tag: metaTags,
		};
		return this.cyclesService.apiService.get<IGetCycleCountResponse>(this.cyclesService.getUrl('count'), objectify(request)).pipe(
			catchError(error => {
				console.error('Error fetching cycle counts:', error);
				return {} as IGetCycleCountResponse; // Return an empty object to avoid undefined errors
			}),
			map(response => {
				const countByList = new Map<CycleListType, number>();
				Object.entries(response).forEach(([key, value]) => {
					countByList.set(capitalize(key) as CycleListType, value);
				});
				return countByList;
			}),
		);
	}

	/**
	 * This service method is used to get a list of cycles.
	 *
	 * @param {CycleListType[]} type - An array of cycle list types.
	 * @param {Date} lastCall - The date of the last call.
	 * @param {Date} [fromDate] - The start date of the range. Optional.
	 * @param {Date} [toDate] - The end date of the range. Optional.
	 *
	 * @returns {Observable<IGetCycleListResponse>} An Observable that will emit the response from the server.
	 *
	 */
	public getLists(type: CycleListType[], lastCall: Date, fromDate?: Date, toDate?: Date): Observable<IGetCycleListResponse> {
		const request: IGetCycleListRequest = {
			list: type.map(t => t.capitalize()),
			lastCall,
			fromDate,
			toDate,
		};
		if (!request.lastCall) delete request.lastCall;
		return this.cyclesService.apiService.get<Partial<Record<CycleListType, ICycle[]>> & {lastCall: Date}>(this.cyclesService.getUrl('list'), objectify(request)).pipe(
			map(response => {
				return Object.entries(response).reduce(
					(acc, [key, value]) => {
						if (key === 'lastCall') {
							acc.lastCall = value as Date;
							return acc;
						}
						acc.lists[key] = value as ICycle[];
						return acc;
					},
					{
						lists: {} as IGetCycleListResponse['lists'],
						lastCall: undefined,
					},
				);
			}),
		);
	}
}
